/**
 * The capture UI runs in platform-renderer, which does not see the shell's
 * `Window` globals. Callers cast through here instead of augmenting `Window`.
 */
export interface CaptureHostWindow {
  hibroCapture?: {
    /** The warmed page is listening for a frame. */
    ready(): void;
    /** The delivered bitmap has decoded. The window stays concealed until the compositor frame matches. */
    painted(): void;
    onFrame(
      cb: (frame: {
        png: Uint8Array;
        width: number;
        height: number;
        scaleFactor: number;
      }) => void,
    ): () => void;
    onReset(cb: () => void): () => void;
    finish(result: {
      action: 'confirm' | 'copy' | 'cancel';
      png?: Uint8Array;
    }): Promise<void>;
    /** Save the PNG. `saved: false` is a cancelled dialog, not a failure. */
    save(png: Uint8Array): Promise<{ saved: boolean }>;
    fail(message: string): Promise<void>;
  };
  hibroClipboard?: {
    /** Write plain text; empty strings count as a failure. */
    writeText(text: string): Promise<boolean>;
    writeImage(bytes: Uint8Array): Promise<boolean>;
  };
  hibroLog?: {
    append(record: {
      level: string;
      kind: string;
      message: string;
      scope?: string;
      error?: { message: string; stack?: string };
    }): void;
  };
}

export function captureHostWindow(): CaptureHostWindow {
  return window as CaptureHostWindow;
}

export function logCapture(message: string, error?: unknown): void {
  const err = error instanceof Error ? error : undefined;
  captureHostWindow().hibroLog?.append({
    level: 'error',
    kind: 'log',
    scope: 'capture',
    message,
    ...(err ? { error: { message: err.message } } : {}),
  });
}
